import { format as formatDate, formatDistanceToNow, isWithinInterval, parseISO, sub } from 'date-fns';
import type { Directive } from 'vue';

const dateTimeFormat: Directive<HTMLElement, string | { date: string; pattern?: string; humanReadable?: boolean; showTimeZone?: boolean }> = {
	beforeMount(el, binding) {
		const date = typeof binding.value === 'string' ? parseISO(binding.value) : parseISO(binding.value.date);
		const pattern = typeof binding.value === 'string' ? 'PP' : binding.value.pattern ?? 'PP';
		const humanReadable = typeof binding.value === 'string' ? false : binding.value.humanReadable ?? false;
		const showTimeZone = typeof binding.value === 'string' ? false : binding.value.showTimeZone ?? false;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		if (humanReadable) {
			el.innerHTML = isWithinInterval(date, {
				start: sub(new Date(), { weeks: 1 }),
				end: new Date(),
			})
				? formatDistanceToNow(date, { addSuffix: true })
				: formatWithOptionalTimeZone(date, pattern, showTimeZone, timeZone);
		} else {
			el.innerHTML = formatWithOptionalTimeZone(date, pattern, showTimeZone, timeZone);
		}
	},
	beforeUpdate(el, binding) {
		const date = typeof binding.value === 'string' ? binding.value : binding.value.date;
		const pattern = typeof binding.value === 'string' ? 'PP' : binding.value.pattern ?? 'PP';
		const humanReadable = typeof binding.value === 'string' ? false : binding.value.humanReadable ?? false;
		const showTimeZone = typeof binding.value === 'string' ? false : binding.value.showTimeZone ?? false;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		if (humanReadable) {
			el.innerHTML = isWithinInterval(parseISO(date), {
				start: sub(new Date(), { weeks: 1 }),
				end: new Date(),
			})
				? formatDistanceToNow(parseISO(date), { addSuffix: true })
				: formatWithOptionalTimeZone(parseISO(date), pattern, showTimeZone, timeZone);
		} else {
			el.innerHTML = formatWithOptionalTimeZone(parseISO(date), pattern, showTimeZone, timeZone);
		}
	},
};

function formatWithOptionalTimeZone(date: Date, pattern: string, showTimeZone: boolean, timeZone: string): string {
	const formattedDate = formatDate(date, pattern);
	if (showTimeZone) {
		const timeZoneAbbr = getTimeZoneAbbr(date, timeZone);
		return `${formattedDate} ${timeZoneAbbr}`;
	}
	return formattedDate;
}

function getTimeZoneAbbr(date: Date, timeZone: string): string {
	const options = { timeZone, timeZoneName: 'short' };
	const formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZoneName: 'short' });
	const parts = formatter.formatToParts(date);
	const timeZoneName = parts.find((part) => part.type === 'timeZoneName');
	return timeZoneName ? timeZoneName.value : '';
}

export default dateTimeFormat;
